import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrushBlob, BrushCircle, BrushLeaf } from './brush/BrushMotifs';
import './LandingBinder.css';

// Public landing — "The Intake Binder". Every capability is a tabbed
// divider sheet in a practitioner's binder; a rail of the same tabs
// runs down the right edge (a mono strip under the nav on phones) and
// the tab for the sheet in view pulls out. Direction contract:
// .impeccable/surfaces/src-components-landingbinder-js.md.
//
// Mounted at /test-landing for Ivan's review (Header.js hides the app
// chrome there). On his go it replaces Landing.js at "/". Copy is the
// incumbent's facts tightened to the product voice; price and trial
// terms are verbatim.

const TABS = [
  { id: 'schedule', label: 'Schedule' },
  { id: 'menus', label: 'Menus' },
  { id: 'booking', label: 'Booking' },
  { id: 'books', label: 'Books' },
  { id: 'clients', label: 'Clients' },
  { id: 'about', label: 'About' },
  { id: 'pricing', label: 'Pricing' },
];

const TRIAL_NOTE = '7 days free, then $39.99/month. No card required.';

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

// The sheet whose top edge has most recently crossed a line a third of
// the way down the viewport is the one being read. Above the first
// sheet the first tab stays pulled out (the brief's first viewport).
function useActiveTab() {
  const [active, setActive] = useState(TABS[0].id);
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const line = window.innerHeight * 0.34;
      let current = TABS[0].id;
      for (const t of TABS) {
        const el = document.getElementById(t.id);
        if (el && el.getBoundingClientRect().top <= line) current = t.id;
      }
      // At the very bottom the last sheet may never reach the line.
      const doc = document.documentElement;
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - 2) current = TABS[TABS.length - 1].id;
      setActive(prev => (prev === current ? prev : current));
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return active;
}

function jumpTo(id) {
  return (e) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (window.history?.replaceState) window.history.replaceState(null, '', `#${id}`);
  };
}

export default function LandingBinder() {
  const active = useActiveTab();
  const stripRef = useRef(null);

  // Keep the current tab in view on the phone strip.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const el = strip.querySelector(`[data-tab="${active}"]`);
    if (el && typeof el.scrollIntoView === 'function' && strip.scrollWidth > strip.clientWidth) {
      el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [active]);

  return (
    <div className="lb av-paper">
      <Nav />
      <nav className="lb-strip" aria-label="Sections" ref={stripRef}>
        {TABS.map(t => (
          <a key={t.id} href={`#${t.id}`} data-tab={t.id} aria-current={active === t.id ? 'true' : undefined} onClick={jumpTo(t.id)}>{t.label}</a>
        ))}
      </nav>

      <div className="lb-body">
        <div className="lb-main">
          <Cover />
          <div className="lb-sheets">
            <ScheduleSheet i={0} />
            <MenusSheet i={1} />
            <BookingSheet i={2} />
            <BooksSheet i={3} />
            <ClientsSheet i={4} />
            <AboutSheet i={5} />
            <PricingSheet i={6} />
          </div>
        </div>

        <nav className="lb-rail" aria-label="Sections">
          {TABS.map(t => (
            <a key={t.id} href={`#${t.id}`} aria-current={active === t.id ? 'true' : undefined} onClick={jumpTo(t.id)}>{t.label}</a>
          ))}
        </nav>
      </div>

      <Footer />
    </div>
  );
}

const Nav = () => (
  <header className="lb-nav">
    <div className="lb-nav-in">
      <Link to="/" className="lb-logo" aria-label="Avayble home">
        <img src="/imgs/avayble_logo.png" alt="Avayble" width="250" height="60" />
      </Link>
      <div className="lb-nav-r">
        <Link to="/login" className="lb-link">Log in</Link>
        <Link to="/signup" className="lb-btn lb-btn-nav">Start free trial</Link>
      </div>
    </div>
  </header>
);

// The binder's cover: the headline, the one sentence, the CTA, and the
// front pocket holding Ivan's note with two watercolor scenes tucked
// behind it.
const Cover = () => (
  <section className="lb-cover" aria-labelledby="lb-h1">
    <div className="lb-cover-copy">
      <h1 id="lb-h1" className="lb-h1">Your massage business. <em>Your rules.</em></h1>
      <p className="lb-lede">Booking software built for the independent massage therapist, not the clinic. It fits the shape your practice already has.</p>
      <div className="lb-cta">
        <Link to="/signup" className="lb-btn lb-btn-lg">Start free trial</Link>
        <p className="lb-cta-note">{TRIAL_NOTE}</p>
      </div>
    </div>

    <div className="lb-pocket" aria-label="A note from Ivan">
      <img className="lb-tuck lb-tuck-a" src="/imgs/landing/illus/pano-left.png" alt="" aria-hidden="true" />
      <img className="lb-tuck lb-tuck-b" src="/imgs/landing/illus/pano-right.png" alt="" aria-hidden="true" />
      <div className="lb-note">
        <p className="lb-note-label">A note from Ivan</p>
        <p>I've been a massage therapist since 2014 — rehab clinics, day spas, hotels, a mobile practice. Nothing on the market was shaped like that work, so I built Avayble.</p>
        <p className="lb-note-sign">— Ivan</p>
      </div>
      <div className="lb-flap" aria-hidden="true" />
    </div>
  </section>
);

const Sheet = ({ id, i, label, flip, motif, children }) => (
  <section id={id} className={`lb-sheet${flip ? ' is-flip' : ''}`} style={{ '--i': i }} aria-labelledby={`${id}-title`}>
    <span className="lb-sheet-tab" aria-hidden="true">{label}</span>
    <span className="lb-motif" aria-hidden="true"><span className="lb-motif-in">{motif}</span></span>
    {children}
  </section>
);

const Caption = ({ left, right }) => (
  <p className="lb-caption"><span>{left}</span>{right && <span>{right}</span>}</p>
);

const ScheduleSheet = ({ i }) => (
  <Sheet id="schedule" i={i} label="Schedule" motif={<BrushBlob width={260} height={260} color="#B07A4E" opacity={0.07} />}>
    <div className="lb-sheet-grid">
      <div className="lb-copy">
        <h2 id="schedule-title" className="lb-h2">Drive-time aware scheduling</h2>
        <p>For the mobile therapist. When a client books, Avayble offers only the times you can actually make — it reads real traffic, drive lengths and rush-hour patterns between appointments.</p>
        <p>No scrambling across town. No double-booked hour. You only accept the sessions you can comfortably reach.</p>
        <img className="lb-scene lb-scene-drive" src="/imgs/landing/illus/scene-driving.png" alt="Therapist driving to an appointment with the route on the dash" loading="lazy" decoding="async" />
      </div>
      <div className="lb-proof">
        <div className="lb-tablet-wrap">
          <Tablet />
          <Caption left="Today's rhythm" right="Illustrative" />
        </div>
      </div>
    </div>
  </Sheet>
);

const Tablet = () => (
  <div className="lb-tablet" aria-hidden="true">
    <div className="lb-tablet-screen">
      <div className="lb-t-head"><span className="lb-t-title">Today's rhythm</span><span className="lb-t-day">Tue</span></div>
      <img className="lb-t-map" src="/imgs/landing/illus/map.png" alt="" />
      <div className="lb-t-appt"><span className="lb-t-time">9:00 AM</span><span><span className="lb-t-name">Diane R.</span><br /><span className="lb-t-meta">90 min · in-home</span></span></div>
      <div className="lb-t-drive">↓ 22 min drive · fits</div>
      <div className="lb-t-appt"><span className="lb-t-time">11:00 AM</span><span><span className="lb-t-name">The Ritz · Rm 412</span><br /><span className="lb-t-meta">60 min · hotel call</span></span></div>
      <div className="lb-t-drive">↓ 14 min drive · fits</div>
      <div className="lb-t-appt"><span className="lb-t-time">1:30 PM</span><span><span className="lb-t-name">Aligned Chiro</span><br /><span className="lb-t-meta">Your rented room · 3 clients</span></span></div>
    </div>
  </div>
);

const MenusSheet = ({ i }) => (
  <Sheet id="menus" i={i} label="Prices" flip motif={<BrushCircle size={200} color="#B07A4E" stroke={6} opacity={0.07} />}>
    <div className="lb-sheet-grid lb-sheet-grid-wide">
      <div className="lb-copy">
        <h2 id="menus-title" className="lb-h2">One price board, every kind of client</h2>
        <p>Your pricing isn't one list. Public prices for your booking page, and prices of their own for the clients who need them:</p>
        <ul className="lb-list">
          <li>A place's own prices: everyone booked at the clinic pays the clinic's.</li>
          <li>Students, a company or your regulars: their own prices, or a discount that follows Public.</li>
          <li>Loyal, long-term clients keep the prices they've always paid.</li>
        </ul>
      </div>
      <div className="lb-proof">
        <img className="lb-shot" src="/imgs/landing/menus-page.png" alt="The Avayble price board" loading="lazy" decoding="async" />
        <Caption left="Prices" right="Capture" />
      </div>
    </div>
  </Sheet>
);

const BookingSheet = ({ i }) => (
  <Sheet id="booking" i={i} label="Booking" motif={<BrushLeaf size={120} color="#B07A4E" opacity={0.08} />}>
    <div className="lb-sheet-grid lb-sheet-grid-wide">
      <div className="lb-copy">
        <h2 id="booking-title" className="lb-h2">Your own booking page</h2>
        <p>Every therapist gets a public booking page. First-time clients see your Public prices and book instantly — their account is created with a text-message code. Regulars sign in to manage their appointments.</p>
        <p>Packages and memberships run through Stripe, so you take payment your way.</p>
      </div>
      <div className="lb-proof">
        <img className="lb-shot" src="/imgs/landing/booking-page.png" alt="An Avayble public booking page as a first-time client sees it" loading="lazy" decoding="async" />
        <Caption left="Public booking page" right="Capture" />
      </div>
    </div>
  </Sheet>
);

const BooksSheet = ({ i }) => (
  <Sheet id="books" i={i} label="Books" flip motif={<BrushBlob width={220} height={220} color="#B07A4E" opacity={0.06} />}>
    <div className="lb-sheet-grid lb-sheet-grid-phone">
      <div className="lb-copy">
        <h2 id="books-title" className="lb-h2">Tax season, simplified</h2>
        <p>Avayble tracks your income, expenses and mileage as you work. When tax season arrives the reports are already there — on your laptop or your phone.</p>
      </div>
      <div className="lb-proof">
        <div className="lb-phone-wrap lb-phone-wrap-sm">
          <Phone src="/imgs/landing/app-reports.png" alt="Avayble income and mileage reports on a phone" />
          <Caption left="Income · mileage" right="Capture" />
        </div>
      </div>
    </div>
  </Sheet>
);

const ClientsSheet = ({ i }) => (
  <Sheet id="clients" i={i} label="Clients" motif={<BrushCircle size={180} color="#B07A4E" stroke={6} opacity={0.07} />}>
    <div className="lb-sheet-grid lb-sheet-grid-phone">
      <div className="lb-copy">
        <h2 id="clients-title" className="lb-h2">Keep the relationship yours</h2>
        <p>Massage is work rooted in human connection. Avayble notices when a client is due to rebook and puts them on your shortlist — then you reach out, from your own phone and email.</p>
        <p>It facilitates the conversation. It never automates the life out of it.</p>
      </div>
      <div className="lb-proof">
        <div className="lb-phone-wrap">
          <Phone src="/imgs/landing/outreach-shortlist.png" alt="The Avayble Smart Shortlist: clients due to rebook" />
          <Caption left="Smart Shortlist" right="Capture" />
        </div>
      </div>
    </div>
  </Sheet>
);

const Phone = ({ src, alt }) => (
  <div className="lb-phone">
    <div className="lb-phone-screen"><img src={src} alt={alt} loading="lazy" decoding="async" /></div>
  </div>
);

const AboutSheet = ({ i }) => (
  <Sheet id="about" i={i} label="About" motif={<BrushLeaf size={140} color="#B07A4E" opacity={0.08} />}>
    <div className="lb-about">
      <div className="lb-copy lb-about-lead">
        <h2 id="about-title" className="lb-h2">You don't fit in a box. Your software shouldn't either.</h2>
        <p>Most booking software is built for clinics. You aren't one. Maybe you rent a room in a day spa, work out of a chiropractic office, do in-home sessions, handle corporate events — or a mix of all four.</p>
        <p>Whatever shape your practice takes, Avayble holds it.</p>
      </div>
      <div className="lb-duo" aria-hidden="true">
        <img src="/imgs/landing/illus/scene-inhome.png" alt="" loading="lazy" decoding="async" />
        <img src="/imgs/landing/illus/scene-clinic.png" alt="" loading="lazy" decoding="async" />
      </div>
      <hr className="lb-rule" />
      <div className="lb-founder">
        <img className="lb-portrait" src="/imgs/landing/illus/ivan-founder.png" alt="Ivan, founder of Avayble" loading="lazy" decoding="async" />
        <div className="lb-copy">
          <h3 className="lb-h3">Built by a therapist, for therapists</h3>
          <p>Avayble was made by a massage therapist nerdy enough to love coding, so your voice carries. Feature requests and bug reports aren't just welcome — they're how the platform gets better.</p>
          <p>Ivan has practiced since 2014: rehab clinics, day spas, lead therapist at luxury hotels, a private mobile practice, and now an app built for the therapist community.</p>
          <p className="lb-tech"><b>On the tech.</b> Avayble is a Progressive Web App. Save it to your iPhone or Android home screen and it behaves like a native app. As the community grows, native apps will follow — with features like importing your phone contacts.</p>
        </div>
      </div>
    </div>
  </Sheet>
);

const PricingSheet = ({ i }) => (
  <Sheet id="pricing" i={i} label="Pricing" motif={<BrushBlob width={240} height={240} color="#B07A4E" opacity={0.07} />}>
    <div className="lb-pricing">
      <div className="lb-copy">
        <h2 id="pricing-title" className="lb-h2">One plan. Everything in it.</h2>
        <p>No hidden fees. No premium tier to unlock.</p>
      </div>
      <div className="lb-invoice" role="group" aria-label="Price">
        <span className="lb-inv-item">Avayble, monthly — absolutely everything</span>
        <span className="lb-inv-dots" aria-hidden="true" />
        <span className="lb-inv-amt">$39.99<span className="lb-inv-per">/mo</span></span>
      </div>
      <div className="lb-cta">
        <Link to="/signup" className="lb-btn lb-btn-lg">Start free trial</Link>
        <p className="lb-cta-note">{TRIAL_NOTE}</p>
      </div>
    </div>
  </Sheet>
);

const Footer = () => (
  <footer className="lb-foot">
    <div className="lb-foot-in">
      <Link to="/" className="lb-foot-logo" aria-label="Avayble home"><img src="/imgs/avayble_logo.png" alt="Avayble" width="250" height="60" /></Link>
      <nav className="lb-foot-links" aria-label="Footer">
        <Link to="/login">Log in</Link>
        <Link to="/signup">Start free trial</Link>
        <a href="/privacy-policy.html">Privacy</a>
        <a href="/terms-of-service.html">Terms</a>
        <a href="/sms-consent-policy.html">SMS Policy</a>
      </nav>
      <p className="lb-foot-copy">© 2026 Avayble LLC</p>
    </div>
  </footer>
);
